import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { useToast } from '../../components/ui/use-toast';
import { confirm } from '../../components/ui/confirm-dialog';
import { useRestaurant } from '../../context/RestaurantContext';
import { ChevronLeft, Plus, Trash2, Search, Filter, Layers } from 'lucide-react';
import {
  listSets, getSet, createSet, updateSet, deleteSet, listCandidates,
} from '../../services/liquorTrackingService';

const money = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const EMPTY = { id: null, name: '', description: '', price_basis: 'cost', dish_ids: [] };

export default function LiquorSets() {
  const navigate = useNavigate();
  const { restaurantId: urlRestaurantId, locationId } = useParams();
  const { restaurantId: ctxRestaurantId } = useRestaurant();
  const restaurantId = urlRestaurantId || ctxRestaurantId;
  const { toast } = useToast();

  const [sets, setSets] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // current set form, or null
  const [selected, setSelected] = useState(new Set()); // dish_ids ticked into the set
  const [saving, setSaving] = useState(false);

  // Price-range selection helper.
  const [search, setSearch] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');

  const loadSets = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      setSets(await listSets(restaurantId));
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Failed to load sets', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [restaurantId, toast]);

  const loadCandidates = useCallback(async () => {
    if (!restaurantId) return;
    try {
      // Price against this location so cost & selling reflect the branch.
      setCandidates(await listCandidates(restaurantId, { location_id: locationId }));
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Failed to load liquors', variant: 'destructive' });
    }
  }, [restaurantId, locationId, toast]);

  useEffect(() => { loadSets(); loadCandidates(); }, [loadSets, loadCandidates]);

  const startNew = () => { setEditing({ ...EMPTY }); setSelected(new Set()); };

  const startEdit = async (id) => {
    try {
      const s = await getSet(restaurantId, id);
      setEditing(s);
      setSelected(new Set(s.dish_ids || []));
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Failed to open set', variant: 'destructive' });
    }
  };

  const toggle = (dishId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(dishId)) next.delete(dishId); else next.add(dishId);
      return next;
    });
  };

  // Visible candidates after search and price-range filter, using the set's chosen price basis.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const basis = editing?.price_basis === 'selling' ? 'selling_price' : 'cost_price';
    const min = minPrice === '' ? null : Number(minPrice);
    const max = maxPrice === '' ? null : Number(maxPrice);
    return candidates.filter((c) => {
      if (q && !`${c.name} ${c.brand}`.toLowerCase().includes(q)) return false;
      const v = Number(c[basis] || 0);
      if (min != null && v < min) return false;
      if (max != null && v > max) return false;
      return true;
    });
  }, [candidates, search, minPrice, maxPrice, editing]);

  // Bulk-pick: add every currently-visible (filtered) liquor to the selection.
  const addAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      visible.forEach((c) => next.add(c.dish_id));
      return next;
    });
  };

  const save = async () => {
    const name = (editing.name || '').trim();
    if (!name) {
      toast({ title: 'Name required', description: 'Give the set a name.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const payload = {
      name,
      description: editing.description || '',
      price_basis: editing.price_basis || 'cost',
      dish_ids: Array.from(selected),
    };
    try {
      if (editing.id) await updateSet(restaurantId, editing.id, payload);
      else await createSet(restaurantId, payload);
      toast({ title: 'Saved', description: `Set "${name}" saved with ${selected.size} item(s).` });
      setEditing(null);
      loadSets();
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Failed to save set', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!(await confirm({ description: `Delete the set "${s.name}"? Stocktakes already run with it are kept.`, confirmText: 'Delete', tone: 'destructive' }))) return;
    try {
      await deleteSet(restaurantId, s.id);
      toast({ title: 'Deleted', description: `Set "${s.name}" removed.` });
      if (editing?.id === s.id) setEditing(null);
      loadSets();
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Failed to delete set', variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 w-full">
      <button
        onClick={() => navigate(`/restaurant/${restaurantId}/locations/${locationId}/liquor-tracking`)}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4"
      >
        <ChevronLeft className="h-4 w-4" /> Liquor Tracking
      </button>

      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-semibold text-foreground tracking-tight leading-none">Liquor Sets</h1>
        </div>
        {!editing && (
          <Button onClick={startNew}><Plus className="h-4 w-4 mr-2" /> New set</Button>
        )}
      </div>

      {!editing ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Layers className="h-5 w-5" /> Your sets</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading…</div>
            ) : sets.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No sets yet. Create one to count a specific group of liquors.</div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {sets.map((s) => (
                  <div key={s.id} className="border rounded-lg p-4 flex flex-col gap-2 hover:border-primary/40 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <button className="text-left" onClick={() => startEdit(s.id)}>
                        <div className="font-display text-lg font-semibold">{s.name}</div>
                        {s.description && <div className="text-sm text-muted-foreground">{s.description}</div>}
                      </button>
                      <Button variant="ghost" size="sm" onClick={() => remove(s)} title="Delete"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                    <div className="text-xs text-muted-foreground mt-auto">{s.item_count} item(s) · basis: {s.price_basis}</div>
                    <Button variant="outline" size="sm" onClick={() => startEdit(s.id)}>Edit</Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>{editing.id ? 'Edit set' : 'New set'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={editing.name} placeholder="e.g. Premium spirits" onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Price basis (for the filter)</Label>
                <Select value={editing.price_basis} onValueChange={(v) => setEditing({ ...editing, price_basis: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cost">Cost price (per bottle)</SelectItem>
                    <SelectItem value="selling">Selling price (menu)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea value={editing.description} rows={2} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </div>

            {/* Selection helper */}
            <div className="rounded-lg border bg-slate-50 p-3 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700"><Filter className="h-4 w-4" /> Filter & bulk-pick</div>
              <div className="grid gap-3 sm:grid-cols-4">
                <div className="relative sm:col-span-2">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input placeholder="Search liquor…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white" />
                </div>
                <Input type="number" min="0" placeholder={`Min ${editing.price_basis}`} value={minPrice} onChange={(e) => setMinPrice(e.target.value)} className="bg-white" />
                <Input type="number" min="0" placeholder={`Max ${editing.price_basis}`} value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="bg-white" />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{visible.length} match · {selected.size} selected</span>
                <Button variant="outline" size="sm" onClick={addAllVisible} disabled={visible.length === 0}>Add all {visible.length} matches</Button>
              </div>
            </div>

            {/* Candidate list */}
            <div className="border rounded-lg max-h-96 overflow-auto divide-y">
              {visible.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">No liquors match. Add liquor items (with prices) under All Dishes.</div>
              ) : visible.map((c) => (
                <label key={c.dish_id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={selected.has(c.dish_id)} onChange={() => toggle(c.dish_id)} className="h-4 w-4" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{c.name}</div>
                    {c.brand && <div className="text-xs text-muted-foreground truncate">{c.brand}</div>}
                  </div>
                  <div className="text-xs text-muted-foreground text-right tabular-nums shrink-0">
                    <div>Cost {money(c.cost_price)}</div>
                    <div>Sell {money(c.selling_price)}</div>
                  </div>
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save set'}</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
